import React, { useState } from 'react';
import { ExperienceErrorBoundary } from '@atlassian/jira-business-experience-tracking/src/controllers/experience-tracker/index.tsx';
import { useExperienceFail } from '@atlassian/jira-experience-tracker/src/ui/experience-fail/index.tsx';
import { useExperienceStart } from '@atlassian/jira-experience-tracker/src/ui/experience-start/index.tsx';
import { useExperienceSuccess } from '@atlassian/jira-experience-tracker/src/ui/experience-success/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { onGenerateCsv } from '@atlassian/jira-software-roadmap-image-export/src/export/csv/index.tsx';
import type { ExportTableProps } from '@atlassian/jira-software-roadmap-image-export/src/export/types.tsx';
import { ExportButton } from '@atlassian/jira-software-roadmap-timeline-table-kit/src/ui/header/export/index.tsx';
import { EXPORT_IMAGE_EXPERIENCE, EXPORT_CSV_EXPERIENCE } from '../../../common/constants.tsx';
import type { CsvProps } from '../../../controllers/timeline-view-issues/types.tsx';
import { ExportImageModal } from './export-image/index.tsx';

type Props = {
	tableProps: ExportTableProps;
	// TODO (HRZ-1493): Make mandatory when cleaning FF up.
	csvProps?: CsvProps;
};

export const Export = ({ csvProps, tableProps }: Props) => {
	const [open, setOpen] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const onCsvFail = useExperienceFail(EXPORT_CSV_EXPERIENCE);
	const onCsvSuccess = useExperienceSuccess(EXPORT_CSV_EXPERIENCE);
	const onCsvStart = useExperienceStart({ ...EXPORT_CSV_EXPERIENCE, analyticsSource: 'timeline' });

	const exportButton = (
		<ExportButton
			onExportCsv={() => {
				if (csvProps) {
					onCsvStart();
					try {
						onGenerateCsv(csvProps);

						onCsvSuccess();
						fireUIAnalytics(createAnalyticsEvent({}), 'csv exported');
					} catch (error) {
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						onCsvFail(EXPORT_CSV_EXPERIENCE.experience, error as Error);
					}
				}
			}}
			onExportAsImageClick={() => setOpen(true)}
			isThemedButton
		/>
	);

	const onOpenChange = (isOpen: boolean) => {
		setOpen(isOpen);
	};

	return (
		<ExperienceErrorBoundary experience={EXPORT_IMAGE_EXPERIENCE}>
			<ExportImageModal open={open} onOpenChange={onOpenChange} tableProps={tableProps} />
			{exportButton}
		</ExperienceErrorBoundary>
	);
};
